import { api } from '../client'
import { ENDPOINTS } from '../config'
import { ParentRecord } from '../types'

/**
 * The three outcomes `POST /parent-duplicates/:id/process` accepts.
 *
 * `accept`  — keep the primary record, move children/check-ins/co-parents onto
 *             it and delete the secondary.
 * `keep_duplicate` — the mirror image: keep the secondary, delete the primary.
 * `reject`  — not a duplicate; both records survive and the pair stops being
 *             offered for review.
 */
export type DuplicateAction = 'accept' | 'reject' | 'keep_duplicate'

/**
 * An embedded `parents` row as the duplicates endpoints send it: the parent
 * record plus the central user fields the backend batches onto it.
 */
export type DuplicateParentRow = ParentRecord & {
    email?: string | null
    middleName?: string | null
}

/**
 * A row from `children_church.parent_duplicates`.
 *
 * `parent` / `duplicate` are the `parents` rows the backend eager-loads, with
 * the `public.users` columns (name, email, phone, last sign-in) batched onto
 * them server-side. What they still lack is the children under each record —
 * that is what `useDuplicateParties` fetches when a pair is opened.
 */
export interface ParentDuplicateRecord {
    _id: string
    /** The earlier of the two records — "primary" throughout the UI. */
    parentId: string
    /** The later record — "secondary" throughout the UI. */
    duplicateId: string
    duplicateReason?: string | null
    /** `decimal(5,2)`, so pg hands it back as a string. */
    similarityScore?: number | string | null
    pendingReview: boolean
    accepted: boolean
    processed: boolean
    processedBy?: string | null
    processedAt?: string | null
    notes?: string | null
    /**
     * The two `parents` rows, eager-loaded. Either side is `null` once it has
     * been merged away: `parent_duplicates` no longer cascades on parent delete
     * (202608220001-FixParentDuplicatesAudit), so the audit row outlives the
     * record it removed — and which side survives is how you tell an `accept`
     * from a `keep_duplicate` after the fact.
     *
     * Name, email, phone and `lastLoginAt` are attached server-side from
     * `public.users` in one batch per listing, so a row can label itself
     * without a lookup. A server that predates that enrichment sends them bare;
     * every consumer here falls back rather than assuming.
     */
    parent?: DuplicateParentRow | null
    duplicate?: DuplicateParentRow | null
    createdAt?: string
    updatedAt?: string
}

export interface RefreshResult {
    /** Pairs newly inserted by this run. */
    newRecords: number
    /**
     * Existing unprocessed pairs whose `duplicateReason` / `similarityScore`
     * this run rewrote. The first run after the audit fix reports a large
     * one-time figure here with `newRecords: 0` while legacy reason strings
     * reconcile, then goes quiet.
     */
    updatedRecords: number
}

export interface ProcessDuplicatePayload {
    action: DuplicateAction
    /** Central `users._id` of whoever decided — stored as `processed_by`. */
    userId: string
    notes?: string
}

export interface ByParentFilters {
    pendingOnly?: boolean
    processedOnly?: boolean
    includeRejected?: boolean
}

/** The DTO transforms with `value === 'true'`, so only the string form works. */
const boolParams = (filters: ByParentFilters = {}) => {
    const params: Record<string, string> = {}
    if (filters.pendingOnly) params.pendingOnly = 'true'
    if (filters.processedOnly) params.processedOnly = 'true'
    if (filters.includeRejected) params.includeRejected = 'true'
    return params
}

export const ParentDuplicatesService = {
    pending: () => api.get<ParentDuplicateRecord[]>(ENDPOINTS.PARENT_DUPLICATES.PENDING),

    processed: () =>
        api.get<ParentDuplicateRecord[]>(ENDPOINTS.PARENT_DUPLICATES.PROCESSED),

    /**
     * Every pair a parent appears in, on either side. This is the only listing
     * endpoint a plain parent can usefully call — `pending` and `processed`
     * return the whole church.
     */
    byParent: (parentId: string, filters: ByParentFilters = {}) =>
        api.get<ParentDuplicateRecord[]>(
            ENDPOINTS.PARENT_DUPLICATES.BY_PARENT(parentId, boolParams(filters))
        ),

    byId: (id: string) =>
        api.get<ParentDuplicateRecord>(ENDPOINTS.PARENT_DUPLICATES.BY_ID(id)),

    /** Re-run detection now instead of waiting for the 03:00 Africa/Lagos cron. */
    refresh: () => api.post<RefreshResult>(ENDPOINTS.PARENT_DUPLICATES.REFRESH),

    process: (id: string, payload: ProcessDuplicatePayload) =>
        api.post<ParentDuplicateRecord>(ENDPOINTS.PARENT_DUPLICATES.PROCESS(id), payload)
}
